import Login from "@/components/Login";
import { useAuth } from "@/context/useAuth";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import i18n from "./i18n";
import moment from "moment";
import numeral from "numeral";
import Loading from "./components/Loading";
import DispositiviPage from "./components/DispositiviPage";
import MainLayout from "./components/MainLayout";
import UtentiPage from "./components/UtentiPage";
import ClientePage from "./components/ClientePage";
import RuoliPermessiPage from "./components/RuoliPermessiPage";
import AziendePage from "./components/AziendePage";
import RecuperoPassword from "./components/RecuperoPassword";
import CompletaRecuperoPassword from "@/components/CompletaRecuperoPassword";
import ProfiloPage from "@/components/ProfiloPage";
import NotFoundPage from "@/components/NotFoundPage";
import CoverLayout from "@/elements/CoverLayout";
import ConfigurazionePage from "@/components/ConfigurazionePage";
import AlloggiamentoFiltroPage from "./components/AlloggiamentoFiltroPage";
import CalendarioInterventiPage from "./components/CalendarioInterventiPage";
import ModelliDispositivoPage from "./components/ModelliDispositivoPage";
import { NodoDetailsPageQRCode, NodoPage } from "./components/NodoPage";
import AziendaGrafoPage from "./components/AziendaGrafoPage";
import HomePageUtenteEsterno from "./components/HomePageUtenteEsterno";
import CalendarioTrasferimentiPage from "./components/CalendarioTrasferimentiPage";
import RichiestaPreventivo from "./components/RichiestaPreventivo";
import RichiestePreventivoPage from "./components/RichiestePreventivoPage";
import RichiestaPreventivoDetailPage from "./components/RichiestaPreventivoDetailPage";

const localeMapForNumeralAndMoment: Record<string, string> = {
  it: "it",
  en: "en-gb",
  de: "de",
};

export default function App() {
  const { t } = useTranslation();

  let { isLoading, user, hasPermission } = useAuth();

  const language = user?.lingua ?? "it";

  useEffect(() => {
    i18n.loadLanguages(language).then(() => {
      i18n.changeLanguage(language);
    });
  }, [language]);

  useEffect(() => {
    const locale = localeMapForNumeralAndMoment[language] ?? "it";
    moment.locale(locale);
    numeral.locale(locale);
  }, [language]);

  if (isLoading) {
    return (
      <div
        style={{
          marginTop: "calc(50vh - 50px)",
        }}
      >
        <Loading withBranding />
      </div>
    );
  }

  return (
    <Routes>
      {/* LOGIN */}
      <Route path="/login" element={<Login />} />
      <Route path="/recupero-password" element={<RecuperoPassword />} />
      <Route
        path="/completa-recupero-password/:resetPermlink/:token"
        element={<CompletaRecuperoPassword />}
      />
      <Route path="/richiesta_preventivo" element={<RichiestaPreventivo />} />

      {/* DASHBOARD PAGES*/}

      <Route
        path="/macchinari"
        element={
          <RequireAuth>
            <MainLayout>
              <DispositiviPage key="macchinario" type="macchinario" />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/sensori"
        element={
          <RequireAuth>
            <MainLayout>
              <DispositiviPage key="sensore" type="sensore" />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/profilo"
        element={
          <RequireAuth>
            <MainLayout>
              <ProfiloPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/aziende"
        element={
          <RequireAuth>
            <MainLayout>
              <AziendePage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/azienda/:id/grafo"
        element={
          <RequireAuth>
            <MainLayout>
              <AziendaGrafoPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      {user?.isEsterno && (
        <Route
          path="/home"
          element={
            <RequireAuth>
              <MainLayout>
                <HomePageUtenteEsterno />
              </MainLayout>
            </RequireAuth>
          }
        />
      )}

      {/* <Route
        path="/aziende/:id"
        element={
          <RequireAuth>
            <MainLayout>
              <AziendaDetailsPage />
            </MainLayout>
          </RequireAuth>
        }
      /> */}
      <Route
        path="/utenti"
        element={
          <RequireAuth>
            <MainLayout>
              <UtentiPage />
            </MainLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/account_aziende"
        element={
          <RequireAuth>
            <MainLayout>
              <ClientePage />
            </MainLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/ruoli_permessi"
        element={
          <RequireAuth>
            <MainLayout>
              <RuoliPermessiPage />
            </MainLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/modelli_dispositivi"
        element={
          <RequireAuth>
            <MainLayout>
              <ModelliDispositivoPage />
            </MainLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/configurazione"
        element={
          <RequireAuth>
            <MainLayout>
              <ConfigurazionePage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/nodi/:id"
        element={
          <RequireAuth>
            <MainLayout>
              <NodoPage />
            </MainLayout>
          </RequireAuth>
        }
      />
      {/*<Route
        path="/centrali/:id"
        element={
          <RequireAuth>
            <MainLayout>
              <CentraleDetailsPage />
            </MainLayout>
          </RequireAuth>
        }
      /> */}
      <Route
        path="/qr/centrale/:token"
        element={
          <RequireAuth>
            <MainLayout>
              <NodoDetailsPageQRCode />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/calendario-interventi"
        element={
          <RequireAuth>
            <MainLayout>
              <CalendarioInterventiPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/calendario-trasferimenti"
        element={
          <RequireAuth>
            <MainLayout>
              <CalendarioTrasferimentiPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/richieste_risparmi"
        element={
          <RequireAuth>
            <MainLayout>
              <RichiestePreventivoPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/richieste_risparmi/:id"
        element={
          <RequireAuth>
            <MainLayout>
              <RichiestaPreventivoDetailPage />
            </MainLayout>
          </RequireAuth>
        }
      />

      <Route
        path="/qr/filtro/:id"
        element={
          user ? (
            <MainLayout>
              <AlloggiamentoFiltroPage />
            </MainLayout>
          ) : (
            <CoverLayout title={t("app.qrTitolo")} bigContent>
              <AlloggiamentoFiltroPage />
            </CoverLayout>
          )
        }
      />
      <Route
        path="/"
        element={
          user ? (
            user.isEsterno ? (
              <Navigate to="/home" />
            ) : hasPermission("DispositiviRead") ? (
              <Navigate to="/macchinari" />
            ) : hasPermission("ContenitoriRead") ? (
              <Navigate to="/aziende" />
            ) : (
              <Navigate to="/not-found" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      <Route
        path="*"
        element={
          user ? (
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          ) : (
            <CoverLayout
              title={t("app.nome")}
              description={t("notFound.descrizione")}
              showLogo
            />
          )
        }
      />
    </Routes>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();

  if (!auth.user) {
    return (
      <Navigate
        to={`/login?redirect=${location.pathname}`}
        state={{ from: location }}
        replace
      />
    );
  }

  return children;
}
